'use client';

import { useCallback, type ReactNode } from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';

import Link from '@/components/ui/Link';
import { track } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import { useWidgetImpression } from './useWidgetImpression';
import { useWidgetIdentity } from './widget-identity';

/**
 * The shell every New-Home widget renders into. Gives the home a single card
 * language (eyebrow label + optional action link, then body) and built-in
 * loading / empty / error states, so each widget stays self-contained: it owns
 * its data and its states, and the grid can place or reorder it without knowing
 * anything about its innards.
 */
export type WidgetState = 'ready' | 'loading' | 'empty' | 'error';

/**
 * Map a widget's SWR triplet to its card state. Every widget shares the same
 * ladder (error → loading → empty → ready); only the "loaded but nothing to
 * show" predicate is per-widget, so it's the one injected piece.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper for the widgets
export function widgetState<T>(
  data: T | undefined,
  error: unknown,
  isLoading: boolean,
  isEmpty: (data: T) => boolean,
): WidgetState {
  if (error) return 'error';
  if (!data) return isLoading ? 'loading' : 'empty';
  return isEmpty(data) ? 'empty' : 'ready';
}

export interface WidgetCardProps {
  /** Uppercase mono label shown top-left, e.g. "RITMO DE GASTOS". */
  eyebrow: string;
  /** Optional ⓘ affordance (a `WidgetInfoButton`) rendered before the action. */
  info?: ReactNode;
  /** Optional link rendered top-right (e.g. "Ver", "Faturas"). */
  action?: { label: string; href: string };
  state?: WidgetState;
  emptyLabel?: string;
  errorLabel?: string;
  /** Minimum body height so a row of widgets keeps a consistent rhythm. */
  minBodyClassName?: string;
  className?: string;
  children?: ReactNode;
}

export function WidgetCard({
  eyebrow,
  info,
  action,
  state = 'ready',
  emptyLabel = 'Sem dados neste ciclo',
  errorLabel = 'Não foi possível carregar',
  minBodyClassName = 'min-h-28',
  className,
  children,
}: WidgetCardProps) {
  const identity = useWidgetIdentity();
  const cardRef = useWidgetImpression(identity);

  const handleActionClick = useCallback(() => {
    if (identity) {
      track('new_home.widget_action', { widget: identity.id, action: 'open_link' });
    }
  }, [identity]);

  return (
    <section
      ref={cardRef}
      className={cn(
        'flex h-full flex-col rounded-3xl border border-border-subtle bg-surface p-5 shadow-sm sm:p-6',
        className,
      )}
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        {/* The ⓘ rides right next to the title (user review), not in the action corner. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <p className="truncate font-mono text-2xs font-medium tracking-[0.14em] text-text-tertiary uppercase">
            {eyebrow}
          </p>
          {info}
        </span>
        {action && (
          <Link
            href={action.href}
            onClick={handleActionClick}
            className="flex shrink-0 items-center gap-0.5 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            {action.label}
            <CaretRightIcon size={12} weight="bold" aria-hidden />
          </Link>
        )}
      </header>

      <div className={cn('flex-1', minBodyClassName)}>
        {state === 'loading' && <WidgetSkeleton />}
        {state === 'error' && <WidgetNotice tone="error">{errorLabel}</WidgetNotice>}
        {state === 'empty' && <WidgetNotice tone="muted">{emptyLabel}</WidgetNotice>}
        {state === 'ready' && children}
      </div>
    </section>
  );
}

function WidgetSkeleton() {
  return (
    <div className="space-y-3" aria-hidden>
      <div className="h-7 w-2/5 animate-pulse rounded-md bg-surface-sunken" />
      <div className="h-3 w-3/5 animate-pulse rounded bg-surface-sunken" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-surface-sunken" />
    </div>
  );
}

function WidgetNotice({ tone, children }: { tone: 'muted' | 'error'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex h-full min-h-24 items-center justify-center rounded-2xl border border-dashed px-4 text-center text-sm',
        tone === 'error'
          ? 'border-error/30 text-error-text'
          : 'border-border-subtle text-text-tertiary',
      )}
    >
      {children}
    </div>
  );
}
